import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RevealHeading, Reveal } from "@/components/Reveal";
import { ShoppingCart, Plus, Minus, X, Loader2, Package, ChevronDown, ExternalLink, Check, Scale, Search } from "lucide-react";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const SOURCE_LABEL = { vice: "Vice", turn14: "Turn14", pulsar: "Pulsar", plm: "PLM" };

function ProductCard({ p, onAdd, testid, compareSelected = false, onToggleCompare = null, compareFull = false, onOpenDetail = null }) {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [opts, setOpts] = useState(() =>
    (p.options || []).reduce((a, o) => ({ ...a, [o.name]: o.values?.[0] || "" }), {})
  );
  const hasVariants = (p.options || []).length > 0;
  const priced = p.variations_priced || [];
  const hasPriced = hasVariants && priced.length > 0;
  const pulsarFallback = p.source === "pulsar" && hasVariants && !hasPriced;

  // Only unprimed Pulsar cards need the network fallback — gate it to visibility to avoid a request storm.
  useEffect(() => {
    if (!pulsarFallback || !cardRef.current) return;
    const el = cardRef.current;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [pulsarFallback]);

  // Fast path: resolve the exact selected combo client-side from the embedded variation map.
  const localQuote = useMemo(() => {
    if (!hasPriced) return undefined;
    for (const v of priced) {
      const vo = v.options || {};
      const ok = Object.entries(vo).every(([k, val]) =>
        val == null || val === "" || val === "Any" ? true : opts[k] === val
      );
      if (ok) return { available: true, price: v.price, variation_id: v.variation_id };
    }
    return { available: false, price: null, variation_id: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPriced, JSON.stringify(opts)]);

  // Fallback: live quote endpoint only when the map hasn't been primed for this product yet.
  const [remoteQuote, setRemoteQuote] = useState(undefined);
  useEffect(() => {
    if (!pulsarFallback || !visible) return;
    let active = true;
    setRemoteQuote(null);
    const t = setTimeout(() => {
      api.post(`/store/pulsar-quote`, { product_id: p.ext_id, options: opts })
        .then((r) => { if (active) setRemoteQuote(r.data); })
        .catch(() => { if (active) setRemoteQuote({ available: false, price: null, variation_id: null }); });
    }, 200);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulsarFallback, visible, JSON.stringify(opts)]);

  const quote = hasPriced ? localQuote : remoteQuote;

  const isExternal = p.source !== "vice";
  const hasBasePrice = !!p.price || !!p.price_max;
  const varLoading = pulsarFallback && remoteQuote === null;
  const noPrice = isExternal && !hasBasePrice && !hasVariants;
  const unavailable = hasVariants && quote && quote.available === false;

  const priceLabel = hasVariants
    ? unavailable
      ? "Selection unavailable"
      : quote && quote.available
      ? money(quote.price)
      : p.price_max && p.price_max !== p.price
      ? `${money(p.price)} – ${money(p.price_max)}`
      : money(p.price)
    : isExternal && !hasBasePrice
    ? (p.source === "plm" ? "Price at PLM" : "Price at Vendor")
    : p.price_max && p.price_max !== p.price
    ? `${money(p.price)} – ${money(p.price_max)}`
    : money(p.price);

  const addPrice = hasVariants ? quote?.price : p.price;
  const addVariation = hasVariants ? quote?.variation_id || null : null;
  const canAdd = p.can_purchase && !!addPrice && addPrice > 0 && (!hasVariants || (quote && quote.available));

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd(p, opts, { price: addPrice, variation_id: addVariation });
  };

  return (
    <div ref={cardRef} data-testid={testid} className="group border-r border-b border-[#222] bg-matte flex flex-col h-full">
      <div className="aspect-square overflow-hidden border-b border-[#222] relative">
        <span className="absolute top-0 left-0 z-10 bg-void/90 text-neon text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 border-r border-b border-[#222]">
          {SOURCE_LABEL[p.source] || p.source}
        </span>
        {onToggleCompare && (
          <button
            data-testid={`${testid}-compare`}
            onClick={onToggleCompare}
            disabled={!compareSelected && compareFull}
            title={compareSelected ? "Remove from compare" : compareFull ? "Compare is full (max 3)" : "Add to compare"}
            className={`absolute top-2 right-2 z-10 flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 border transition-colors disabled:opacity-40 ${
              compareSelected ? "bg-neon text-void border-neon" : "bg-void/85 text-[#9CA3AF] border-[#333] hover:text-neon hover:border-neon"
            }`}
          >
            {compareSelected ? <Check size={12} /> : <Scale size={12} />} Compare
          </button>
        )}
        {p.image ? (
          <img src={p.image} alt={p.name} loading="lazy" onClick={onOpenDetail || undefined} className={`w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500 ${onOpenDetail ? "cursor-pointer" : ""}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#333]"><Package size={48} /></div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">{p.series || p.brand || p.category}</span>
          {p.hp ? (
            <span data-testid={`${testid}-hp`} className="text-[10px] font-bold uppercase tracking-[0.1em] text-neon border border-neon/40 px-1.5 py-0.5 shrink-0">
              {p.hp} HP
            </span>
          ) : null}
        </div>
        <h3
          data-testid={`${testid}-title`}
          onClick={onOpenDetail || undefined}
          className={`font-grotesk font-bold text-base text-[#FAFAFA] leading-tight ${onOpenDetail ? "cursor-pointer hover:text-neon transition-colors" : ""}`}
        >
          {p.name}
        </h3>
        {p.description && <p title={p.description} className="mt-2 font-mono text-xs text-[#9CA3AF] leading-relaxed line-clamp-2">{p.description}</p>}

        {(p.options || []).length > 0 && (
          <div className="mt-4 space-y-2">
            {p.options.map((o) => (
              <div key={o.name}>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-1">{o.name}</label>
                <div className="relative">
                  <select
                    data-testid={`${testid}-opt-${o.name}`}
                    value={opts[o.name]}
                    onChange={(e) => setOpts((s) => ({ ...s, [o.name]: e.target.value }))}
                    className="w-full appearance-none bg-void border border-[#222] px-3 py-2 pr-8 font-mono text-xs text-[#FAFAFA] outline-none focus:border-neon rounded-none"
                  >
                    {[...new Set(o.values)].map((v) => <option key={v} value={v} className="bg-void">{v}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto pt-5 flex items-center justify-between gap-3">
          <span data-testid={`${testid}-price`} className={`font-display leading-none ${noPrice ? "text-sm text-[#9CA3AF]" : "text-2xl text-neon"}`}>{varLoading ? "…" : priceLabel}</span>
          <div className="flex items-center gap-2">
            {p.url && !hasVariants && (
              <a href={p.url} target="_blank" rel="noreferrer" data-testid={`${testid}-view`} className="border border-[#333] p-2.5 text-[#9CA3AF] hover:text-neon hover:border-neon transition-colors" title="View at vendor">
                <ExternalLink size={14} />
              </a>
            )}
            {noPrice ? (
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testid}-vendor`}
                className="flex items-center gap-1.5 border border-neon text-neon font-bold uppercase tracking-[0.1em] text-xs py-2.5 px-4 hover:bg-neon hover:text-void transition-colors"
              >
                View Price <ExternalLink size={13} />
              </a>
            ) : (
              <button
                data-testid={`${testid}-add`}
                disabled={!canAdd}
                onClick={handleAdd}
                className="flex items-center gap-1.5 bg-neon text-void font-bold uppercase tracking-[0.1em] text-xs py-2.5 px-4 hover:bg-[#FAFAFA] transition-colors disabled:opacity-40"
              >
                <Plus size={13} /> Add
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Resolve the default option combo price/variation for a turbo (client-side, from embedded map).
function resolveDefault(p) {
  const opts = (p.options || []).reduce((a, o) => ({ ...a, [o.name]: o.values?.[0] || "" }), {});
  if (p.source !== "pulsar" || !(p.options || []).length) {
    return { opts, price: p.price ?? null, variation_id: null, available: p.can_purchase && p.price != null };
  }
  for (const v of p.variations_priced || []) {
    const vo = v.options || {};
    const ok = Object.entries(vo).every(([k, val]) =>
      val == null || val === "" || val === "Any" ? true : opts[k] === val
    );
    if (ok) return { opts, price: v.price, variation_id: v.variation_id, available: true };
  }
  return { opts, price: null, variation_id: null, available: false };
}

const CMP_ROWS = [
  { label: "Series", get: (p) => p.series || "—" },
  { label: "HP Rating", get: (p) => (p.hp ? `${p.hp} HP` : "Not stated") },
  { label: "Starting Price", get: (p) => (p.price != null ? money(p.price) : "—") },
  { label: "Options", get: (p) => (p.options || []).map((o) => o.name).join(", ") || "None" },
];

function CompareModal({ items, onClose, onAdd, onRemove }) {
  return (
    <motion.div
      data-testid="compare-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-void/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 28 }}
        className="w-full max-w-[1100px] bg-matte border border-[#222]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#222] px-6 py-5">
          <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight">Compare Turbos</h3>
          <button data-testid="compare-close" onClick={onClose} className="text-[#9CA3AF] hover:text-neon transition-colors"><X size={22} /></button>
        </div>
        <div className="overflow-x-auto">
          <div className="grid" style={{ gridTemplateColumns: `160px repeat(${items.length}, minmax(200px, 1fr))` }}>
            {/* header row: images + names */}
            <div className="border-b border-r border-[#222] bg-void" />
            {items.map((p) => {
              const tid = `${p.source}-${p.ext_id}`;
              return (
                <div key={tid} className="border-b border-r border-[#222] p-4 relative">
                  <button data-testid={`compare-remove-${tid}`} onClick={() => onRemove(p)} className="absolute top-2 right-2 text-[#9CA3AF] hover:text-neon transition-colors"><X size={16} /></button>
                  <div className="aspect-square bg-void border border-[#222] overflow-hidden mb-3">
                    {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#333]"><Package size={40} /></div>}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neon mb-1">{p.brand}</div>
                  <div title={p.name} className="font-grotesk font-bold text-sm text-[#FAFAFA] leading-tight line-clamp-3">{p.name}</div>
                </div>
              );
            })}
            {/* spec rows */}
            {CMP_ROWS.map((row) => (
              <FragmentRow key={row.label} label={row.label} items={items} get={row.get} />
            ))}
            {/* add-to-cart row */}
            <div className="border-b border-r border-[#222] px-4 py-4 text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] flex items-center bg-void">Action</div>
            {items.map((p) => {
              const tid = `${p.source}-${p.ext_id}`;
              const res = resolveDefault(p);
              return (
                <div key={tid} className="border-b border-r border-[#222] p-4">
                  {res.available && res.price ? (
                    <button
                      data-testid={`compare-add-${tid}`}
                      onClick={() => onAdd(p, res.opts, { price: res.price, variation_id: res.variation_id })}
                      className="w-full flex items-center justify-center gap-1.5 bg-neon text-void font-bold uppercase tracking-[0.1em] text-xs py-2.5 px-3 hover:bg-[#FAFAFA] transition-colors"
                    >
                      <Plus size={13} /> {money(res.price)}
                    </button>
                  ) : (
                    <div className="text-[10px] font-mono text-[#9CA3AF] leading-relaxed">Configure options on the product card to add.</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function FragmentRow({ label, items, get }) {
  return (
    <>
      <div className="border-b border-r border-[#222] px-4 py-4 text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] flex items-center bg-void">{label}</div>
      {items.map((p) => (
        <div key={`${p.source}-${p.ext_id}`} className="border-b border-r border-[#222] px-4 py-4 font-mono text-xs text-[#FAFAFA]">{get(p)}</div>
      ))}
    </>
  );
}


function DetailModal({ p, onClose, onAdd }) {
  const [opts, setOpts] = useState(() =>
    (p.options || []).reduce((a, o) => ({ ...a, [o.name]: o.values?.[0] || "" }), {})
  );
  const [detail, setDetail] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  const hasVariants = (p.options || []).length > 0;
  const priced = p.variations_priced || [];
  const hasPriced = hasVariants && priced.length > 0;
  const pulsarFallback = p.source === "pulsar" && hasVariants && !hasPriced;

  useEffect(() => {
    let active = true;
    api.get(`/store/product-detail`, { params: { source: p.source, ext_id: p.ext_id, url: p.url || "" } })
      .then((r) => { if (active) setDetail(r.data); })
      .catch(() => { if (active) setDetail({ description: "", images: [] }); });
    return () => { active = false; };
  }, [p.source, p.ext_id, p.url]);

  const localQuote = useMemo(() => {
    if (!hasPriced) return undefined;
    for (const v of priced) {
      const vo = v.options || {};
      const ok = Object.entries(vo).every(([k, val]) =>
        val == null || val === "" || val === "Any" ? true : opts[k] === val);
      if (ok) return { available: true, price: v.price, variation_id: v.variation_id };
    }
    return { available: false, price: null, variation_id: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPriced, JSON.stringify(opts)]);

  const [remoteQuote, setRemoteQuote] = useState(undefined);
  useEffect(() => {
    if (!pulsarFallback) return;
    let active = true; setRemoteQuote(null);
    api.post(`/store/pulsar-quote`, { product_id: p.ext_id, options: opts })
      .then((r) => { if (active) setRemoteQuote(r.data); })
      .catch(() => { if (active) setRemoteQuote({ available: false, price: null, variation_id: null }); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulsarFallback, JSON.stringify(opts)]);

  const quote = hasPriced ? localQuote : remoteQuote;
  const unavailable = hasVariants && quote && quote.available === false;
  const loadingPrice = pulsarFallback && remoteQuote === null;
  const priceVal = hasVariants ? quote?.price : p.price;
  const canAdd = p.can_purchase && !!priceVal && priceVal > 0 && (!hasVariants || (quote && quote.available));
  const priceLabel = loadingPrice ? "…" : unavailable ? "Selection unavailable"
    : priceVal != null ? money(priceVal)
    : p.price_max && p.price_max !== p.price ? `${money(p.price)} – ${money(p.price_max)}` : money(p.price);

  const images = (detail?.images && detail.images.length ? detail.images : (p.image ? [p.image] : []));
  const desc = detail === null ? "Loading details…" : (detail.description || p.description || "No additional details provided by the vendor.");

  return (
    <motion.div
      data-testid="detail-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-void/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 28 }}
        className="w-full max-w-[1050px] bg-matte border border-[#222]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#222] px-6 py-4">
          <span className="text-[10px] uppercase tracking-[0.25em] text-neon">{SOURCE_LABEL[p.source] || p.source}</span>
          <button data-testid="detail-close" onClick={onClose} className="text-[#9CA3AF] hover:text-neon transition-colors"><X size={22} /></button>
        </div>
        <div className="grid md:grid-cols-2">
          <div className="border-r border-[#222] p-6">
            <div className="aspect-square bg-void border border-[#222] overflow-hidden">
              {images[imgIdx] ? <img src={images[imgIdx]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#333]"><Package size={56} /></div>}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {images.slice(0, 8).map((src, i) => (
                  <button key={src + i} data-testid={`detail-thumb-${i}`} onClick={() => setImgIdx(i)} className={`w-14 h-14 border overflow-hidden ${i === imgIdx ? "border-neon" : "border-[#333]"}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 flex flex-col">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">{p.brand}</span>
              {p.series && <span className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] border border-[#333] px-1.5 py-0.5">{p.series}</span>}
              {p.vehicle && p.vehicle !== "Universal" && <span className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] border border-[#333] px-1.5 py-0.5">{p.vehicle}</span>}
              {p.hp ? <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-neon border border-neon/40 px-1.5 py-0.5">{p.hp} HP</span> : null}
            </div>
            <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-none mb-4">{p.name}</h2>

            {hasVariants && (
              <div className="space-y-3 mb-4">
                {p.options.map((o) => (
                  <div key={o.name}>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-1">{o.name}</label>
                    <div className="relative">
                      <select
                        data-testid={`detail-opt-${o.name}`}
                        value={opts[o.name]}
                        onChange={(e) => setOpts((s) => ({ ...s, [o.name]: e.target.value }))}
                        className="w-full appearance-none bg-void border border-[#222] px-3 py-2.5 pr-8 font-mono text-xs text-[#FAFAFA] outline-none focus:border-neon"
                      >
                        {[...new Set(o.values)].map((v) => <option key={v} value={v} className="bg-void">{v}</option>)}
                      </select>
                      <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-b border-[#222] py-4 my-2">
              <span data-testid="detail-price" className={`font-display leading-none ${unavailable ? "text-sm text-[#9CA3AF]" : "text-3xl text-neon"}`}>{priceLabel}</span>
              <button
                data-testid="detail-add"
                disabled={!canAdd}
                onClick={() => canAdd && onAdd(p, opts, { price: priceVal, variation_id: hasVariants ? quote?.variation_id || null : null })}
                className="flex items-center gap-2 bg-neon text-void font-bold uppercase tracking-[0.15em] text-sm py-3 px-6 hover:bg-[#FAFAFA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={15} /> Add to Cart
              </button>
            </div>

            <div className="mt-3 overflow-y-auto max-h-[220px] pr-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Details</div>
              <p data-testid="detail-description" className="font-mono text-xs text-[#C7C7C7] leading-relaxed whitespace-pre-line">{desc}</p>
            </div>
            {p.url && (
              <a href={p.url} target="_blank" rel="noreferrer" data-testid="detail-vendor-link" className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-neon transition-colors">
                <ExternalLink size={12} /> View source listing
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}


export default function Performance() {
  const [ymm, setYmm] = useState({ years: [], makes: {} });
  const [sel, setSel] = useState({ year: "", make: "", model: "" });
  const [data, setData] = useState({ fitted: [], universal: [], counts: null });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [cat, setCat] = useState("All");
  const [series, setSeries] = useState("All");
  const [hpRange, setHpRange] = useState("All");
  const [sortBy, setSortBy] = useState("");
  const [search, setSearch] = useState("");
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const { items, add, setQty, remove, count, total, keyOf } = useCart();

  const cmpKey = (p) => `${p.source}-${p.ext_id}`;
  const toggleCompare = (p) => {
    const k = cmpKey(p);
    setCompare((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : cur.length >= 3 ? cur : [...cur, k]));
  };

  useEffect(() => {
    api.get("/store/ymm").then((r) => setYmm(r.data)).catch(() => {});
  }, []);

  const loadParts = useCallback(async (s) => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/parts", { params: { year: s.year, make: s.make, model: s.model } });
      setData(data);
    } catch {
      toast.error("Failed to load parts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadParts({ year: "", make: "", model: "" }); }, [loadParts]);

  const models = sel.make ? (ymm.makes[sel.make] || []) : [];

  const applyYmm = () => loadParts(sel);
  const resetYmm = () => { setSel({ year: "", make: "", model: "" }); loadParts({ year: "", make: "", model: "" }); };

  const onAdd = (p, selected_options, override = {}) => {
    add({
      source: p.source, ext_id: p.ext_id || "", product_id: p.source === "vice" ? p.ext_id : "",
      name: p.name, price: override.price != null ? override.price : p.price, image: p.image,
      selected_options: selected_options || {}, variation_id: override.variation_id || null,
    });
    toast.success("Added to cart");
  };

  const checkout = async () => {
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const { data } = await api.post("/store/checkout", {
        origin_url: window.location.origin,
        items: items.map((i) => ({
          source: i.source, ext_id: i.ext_id, product_id: i.product_id, name: i.name,
          quantity: i.quantity, selected_options: i.selected_options || {}, variation_id: i.variation_id || null,
        })),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setCheckingOut(false);
    }
  };

  const vehicleActive = sel.make;

  const CATS = ["All", "Turbo", "Turbo Parts", "Engine", "Intake", "Exhaust", "Fueling", "Cooling", "Suspension", "Drivetrain", "Brakes", "Interior", "Exterior", "Accessories", "Other"];
  const present = new Set([...data.fitted, ...data.universal].map((p) => p.type));
  const cats = CATS.filter((c) => c === "All" || present.has(c));

  // Turbo-only sub-filters: series/family and HP rating range.
  const SERIES_ORDER = ["PTA Series", "PTC Series", "PTE", "PTG Series", "PTX Gen3", "PTX Gen2", "PSR 400 Series", "PSR 300 Series", "PT/X Gen1"];
  const HP_RANGES = [
    { label: "All HP", value: "All", min: 0, max: Infinity },
    { label: "Up to 800 HP", value: "0-800", min: 0, max: 800 },
    { label: "800–1200 HP", value: "800-1200", min: 800, max: 1200 },
    { label: "1200–1800 HP", value: "1200-1800", min: 1200, max: 1800 },
    { label: "1800+ HP", value: "1800+", min: 1800, max: Infinity },
  ];
  const turbos = data.universal.filter((p) => p.type === "Turbo");
  const seriesList = SERIES_ORDER.filter((s) => turbos.some((p) => p.series === s));

  const applyTurboFilters = (list) => {
    if (cat !== "Turbo") return list;
    const hp = HP_RANGES.find((x) => x.value === hpRange) || HP_RANGES[0];
    return list.filter((p) => {
      if (series !== "All" && p.series !== series) return false;
      if (hpRange !== "All" && (!p.hp || p.hp < hp.min || p.hp >= hp.max)) return false;
      return true;
    });
  };

  const byCat = (list) =>
    cat === "All"
      ? list.filter((p) => p.type !== "Turbo" && p.type !== "Turbo Parts")
      : list.filter((p) => p.type === cat);

  const searchQ = search.trim().toLowerCase();
  const bySearch = (list) => {
    if (!searchQ) return list;
    return list.filter((p) => {
      const hay = [p.name, p.brand, p.description, p.part_number, p.ext_id, p.series, p.category,
        ...(p.options || []).flatMap((o) => [o.name, ...(o.values || [])])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(searchQ);
    });
  };

  const SORTS = [
    { value: "", label: "Featured" },
    { value: "price-asc", label: "Price (low to high)" },
    { value: "price-desc", label: "Price (high to low)" },
    { value: "hp-asc", label: "HP Rating (lowest to highest)" },
    { value: "hp-desc", label: "HP Rating (highest to lowest)" },
  ];
  const sortList = (list) => {
    if (!sortBy) return list;
    const arr = [...list];
    arr.sort((a, b) => {
      if (sortBy.startsWith("price")) {
        const pa = a.price == null ? Infinity : Number(a.price);
        const pb = b.price == null ? Infinity : Number(b.price);
        return sortBy === "price-asc" ? pa - pb : pb - pa;
      }
      // HP: keep unrated turbos at the end for both directions
      const va = a.hp == null ? (sortBy === "hp-asc" ? Infinity : -1) : a.hp;
      const vb = b.hp == null ? (sortBy === "hp-asc" ? Infinity : -1) : b.hp;
      return sortBy === "hp-asc" ? va - vb : vb - va;
    });
    return arr;
  };

  const fittedF = sortList(byCat(bySearch(data.fitted)));
  const universalF = sortList(applyTurboFilters(byCat(bySearch(data.universal))));

  const allProducts = [...data.fitted, ...data.universal];
  const compareItems = compare.map((k) => allProducts.find((p) => cmpKey(p) === k)).filter(Boolean);
  const canCompare = (p) => p.type === "Turbo";
  const removeCompare = (p) => setCompare((cur) => cur.filter((x) => x !== cmpKey(p)));

  return (
    <div className="bg-void min-h-screen" data-testid="performance-page">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-8 border-b border-[#222]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-neon mb-5 flex items-center gap-3">
              <span className="w-8 h-px bg-neon" /> The Store
            </div>
            <h1 className="font-display uppercase text-6xl md:text-7xl lg:text-[8vw] leading-[0.82] tracking-tight">
              <RevealHeading lines={["Performance", "Parts"]} />
            </h1>
          </div>
          <button data-testid="open-cart-btn" onClick={() => setOpen(true)} className="relative flex items-center gap-3 border border-[#333] px-6 py-4 hover:border-neon hover:text-neon transition-colors uppercase tracking-[0.2em] text-xs w-fit">
            <ShoppingCart size={18} /> Cart
            {count > 0 && <span className="bg-neon text-void font-bold px-2 py-0.5 text-[10px]">{count}</span>}
          </button>
        </div>
      </section>

      {/* YMM SELECTOR — first interaction */}
      <section className="border-b border-[#222] bg-[#0A0010]" data-testid="ymm-selector">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#9CA3AF] mb-4">Find parts for your vehicle</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr_1.5fr_auto_auto] gap-3">
            <div className="relative">
              <select data-testid="ymm-year" value={sel.year} onChange={(e) => setSel((s) => ({ ...s, year: e.target.value }))} className="w-full appearance-none bg-matte border border-[#222] px-4 py-4 pr-9 font-mono text-sm text-[#FAFAFA] outline-none focus:border-neon rounded-none uppercase tracking-wide">
                <option value="">Year</option>
                {ymm.years.map((y) => <option key={y} value={y} className="bg-void">{y}</option>)}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-neon pointer-events-none" />
            </div>
            <div className="relative">
              <select data-testid="ymm-make" value={sel.make} onChange={(e) => setSel((s) => ({ ...s, make: e.target.value, model: "" }))} className="w-full appearance-none bg-matte border border-[#222] px-4 py-4 pr-9 font-mono text-sm text-[#FAFAFA] outline-none focus:border-neon rounded-none uppercase tracking-wide">
                <option value="">Make</option>
                {Object.keys(ymm.makes).map((m) => <option key={m} value={m} className="bg-void">{m}</option>)}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-neon pointer-events-none" />
            </div>
            <div className="relative">
              <select data-testid="ymm-model" value={sel.model} disabled={!sel.make} onChange={(e) => setSel((s) => ({ ...s, model: e.target.value }))} className="w-full appearance-none bg-matte border border-[#222] px-4 py-4 pr-9 font-mono text-sm text-[#FAFAFA] outline-none focus:border-neon rounded-none uppercase tracking-wide disabled:opacity-40">
                <option value="">{sel.make ? "Model" : "Select make first"}</option>
                {models.map((m) => <option key={m} value={m} className="bg-void">{m}</option>)}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-neon pointer-events-none" />
            </div>
            <button data-testid="ymm-search" onClick={applyYmm} disabled={!sel.make} className="bg-neon text-void font-bold uppercase tracking-[0.2em] text-xs py-4 px-8 hover:bg-[#FAFAFA] transition-colors disabled:opacity-40">
              Search
            </button>
            {vehicleActive && (
              <button data-testid="ymm-reset" onClick={resetYmm} className="border border-[#333] uppercase tracking-[0.2em] text-xs py-4 px-6 hover:border-neon hover:text-neon transition-colors">
                Reset
              </button>
            )}
          </div>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="border-b border-[#222] bg-void" data-testid="store-search">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-4">
          <div className="relative max-w-xl">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
            <input
              data-testid="store-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search part name or part number…"
              className="w-full bg-matte border border-[#222] pl-11 pr-10 py-3.5 font-mono text-sm text-[#FAFAFA] placeholder:text-[#555] outline-none focus:border-neon rounded-none"
            />
            {search && (
              <button
                data-testid="store-search-clear"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-neon transition-colors"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-[#222] bg-void sticky top-16 md:top-20 z-30" data-testid="category-filter">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-4 flex gap-2 overflow-x-auto no-scrollbar">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`shrink-0 uppercase tracking-[0.2em] text-xs py-2.5 px-5 border transition-colors ${
                cat === c ? "bg-neon text-void border-neon font-bold" : "border-[#333] text-[#9CA3AF] hover:border-neon hover:text-neon"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* TURBO SUB-FILTERS: series + HP range */}
      {cat === "Turbo" && (
        <section className="border-b border-[#222] bg-[#0A0010]" data-testid="turbo-filter">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] shrink-0">Filter turbos</span>
            <div className="relative w-full sm:w-64">
              <select
                data-testid="turbo-series-select"
                value={series}
                onChange={(e) => setSeries(e.target.value)}
                className="w-full appearance-none bg-matte border border-[#222] px-4 py-3 pr-9 font-mono text-xs text-[#FAFAFA] outline-none focus:border-neon rounded-none uppercase tracking-wide"
              >
                <option value="All" className="bg-void">All Series / Sizes</option>
                {seriesList.map((s) => <option key={s} value={s} className="bg-void">{s}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-neon pointer-events-none" />
            </div>
            <div className="relative w-full sm:w-56">
              <select
                data-testid="turbo-hp-select"
                value={hpRange}
                onChange={(e) => setHpRange(e.target.value)}
                className="w-full appearance-none bg-matte border border-[#222] px-4 py-3 pr-9 font-mono text-xs text-[#FAFAFA] outline-none focus:border-neon rounded-none uppercase tracking-wide"
              >
                {HP_RANGES.map((r) => <option key={r.value} value={r.value} className="bg-void">{r.label}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-neon pointer-events-none" />
            </div>
            {(series !== "All" || hpRange !== "All") && (
              <button
                data-testid="turbo-filter-reset"
                onClick={() => { setSeries("All"); setHpRange("All"); }}
                className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-neon transition-colors shrink-0"
              >
                Clear
              </button>
            )}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1600px] px-5 md:px-10 py-12 md:py-16">
        {loading ? (
          <div className="flex items-center gap-2 font-mono text-[#9CA3AF] uppercase tracking-[0.2em] text-sm"><Loader2 className="animate-spin" size={16} /> Loading parts…</div>
        ) : (
          <>
            {/* SORT BY */}
            <div className="flex items-center justify-end gap-3 mb-8" data-testid="sort-bar">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] shrink-0">Sort by</span>
              <div className="relative w-full sm:w-72">
                <select
                  data-testid="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full appearance-none bg-matte border border-[#222] px-4 py-3 pr-9 font-mono text-xs text-[#FAFAFA] outline-none focus:border-neon rounded-none uppercase tracking-wide"
                >
                  {SORTS.map((s) => <option key={s.value} value={s.value} className="bg-void">{s.label}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-neon pointer-events-none" />
              </div>
            </div>
            {/* Fits your vehicle */}
            {vehicleActive && (
              <div className="mb-16" data-testid="fitted-section">
                <div className="flex items-baseline justify-between mb-6 border-b border-[#222] pb-4">
                  <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight">
                    Fits Your <span className="text-neon">{sel.year} {sel.make} {sel.model}</span>
                  </h2>
                  <span className="font-mono text-xs text-[#9CA3AF]">{fittedF.length} parts</span>
                </div>
                {fittedF.length === 0 ? (
                  <div className="font-mono text-sm text-[#9CA3AF] border border-[#222] p-8" data-testid="fitted-empty">
                    {searchQ
                      ? `No parts match "${search.trim()}" for your selected model. Try a different name or part number.`
                      : "There are no compatible parts in the current catalogue that fit your selected model."}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#222]">
                    {fittedF.map((p, i) => <ProductCard key={p.ext_id + i} p={p} onAdd={onAdd} testid={`fitted-${p.source}-${p.ext_id}`} compareSelected={compare.includes(cmpKey(p))} onToggleCompare={canCompare(p) ? () => toggleCompare(p) : null} compareFull={compare.length >= 3} onOpenDetail={() => setDetailProduct(p)} />)}
                  </div>
                )}
              </div>
            )}

            {/* Universal */}
            <div data-testid="universal-section">
              <div className="flex items-baseline justify-between mb-6 border-b border-[#222] pb-4">
                <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight">Universal / All Vehicles</h2>
                <span className="font-mono text-xs text-[#9CA3AF]">{universalF.length} parts</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#222]">
                {universalF.map((p, i) => (
                  <Reveal key={(p.ext_id || p.name) + i} delay={(i % 4) * 0.04}>
                    <ProductCard p={p} onAdd={onAdd} testid={`universal-${p.source}-${p.ext_id}`} compareSelected={compare.includes(cmpKey(p))} onToggleCompare={canCompare(p) ? () => toggleCompare(p) : null} compareFull={compare.length >= 3} onOpenDetail={() => setDetailProduct(p)} />
                  </Reveal>
                ))}
              </div>
              {universalF.length === 0 && (
                <div data-testid="universal-empty" className="font-mono text-sm text-[#9CA3AF] border border-[#222] p-8">
                  {searchQ
                    ? `No parts match "${search.trim()}". Try a different name or part number.`
                    : cat === "Turbo"
                      ? "No turbos match this series / HP combination. Try widening the HP range or choosing another series."
                      : cat === "All"
                        ? "No universal parts to show here. Select the Turbo or Turbo Parts category to browse forced-induction options."
                        : `No universal ${cat.toLowerCase()} parts available right now.`}
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Compare bar */}
      <AnimatePresence>
        {compare.length > 0 && (
          <motion.div
            data-testid="compare-bar"
            initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#222] bg-matte/95 backdrop-blur"
          >
            <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-4 flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-neon shrink-0 flex items-center gap-2"><Scale size={14} /> Compare</span>
              <div className="flex items-center gap-2 flex-1 overflow-x-auto no-scrollbar">
                {compareItems.map((p) => (
                  <div key={cmpKey(p)} className="relative shrink-0 w-12 h-12 border border-[#333] bg-void overflow-hidden group">
                    {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : null}
                    <button data-testid={`compare-bar-remove-${cmpKey(p)}`} onClick={() => removeCompare(p)} className="absolute inset-0 flex items-center justify-center bg-void/70 opacity-0 hover:opacity-100 transition-opacity text-neon"><X size={14} /></button>
                  </div>
                ))}
                <span className="font-mono text-[10px] text-[#9CA3AF] ml-1">{compare.length}/3 selected</span>
              </div>
              <button data-testid="compare-clear" onClick={() => setCompare([])} className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-neon transition-colors shrink-0">Clear</button>
              <button
                data-testid="compare-open"
                onClick={() => setShowCompare(true)}
                disabled={compare.length < 2}
                className="bg-neon text-void font-bold uppercase tracking-[0.15em] text-xs py-3 px-6 hover:bg-[#FAFAFA] transition-colors disabled:opacity-40 shrink-0"
              >
                Compare ({compare.length})
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCompare && compareItems.length >= 2 && (
          <CompareModal
            items={compareItems}
            onClose={() => setShowCompare(false)}
            onAdd={(p, o, ov) => { onAdd(p, o, ov); }}
            onRemove={removeCompare}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detailProduct && (
          <DetailModal
            p={detailProduct}
            onClose={() => setDetailProduct(null)}
            onAdd={(p, o, ov) => { onAdd(p, o, ov); }}
          />
        )}
      </AnimatePresence>

      <Footer />

      {/* Cart drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-void/80" onClick={() => setOpen(false)} />
            <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.4 }} data-testid="cart-drawer" className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-matte border-l border-[#222] flex flex-col">
              <div className="p-6 border-b border-[#222] flex items-center justify-between">
                <span className="font-display uppercase text-2xl tracking-tight">Your Cart</span>
                <button data-testid="close-cart-btn" onClick={() => setOpen(false)}><X className="text-[#9CA3AF] hover:text-neon" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {items.length === 0 ? (
                  <div className="font-mono text-sm text-[#9CA3AF]">Your cart is empty.</div>
                ) : (
                  items.map((i) => {
                    const k = keyOf(i);
                    return (
                      <div key={k} data-testid={`cart-item-${i.source}-${i.ext_id}`} className="flex gap-4 border border-[#222] p-3">
                        {i.image && <img src={i.image} alt={i.name} className="w-16 h-16 object-cover grayscale" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-neon">{SOURCE_LABEL[i.source]}</span>
                          </div>
                          <div title={i.name} className="font-grotesk font-bold text-sm text-[#FAFAFA] truncate">{i.name}</div>
                          {i.selected_options && Object.keys(i.selected_options).length > 0 && (
                            <div title={Object.values(i.selected_options).join(" · ")} className="font-mono text-[10px] text-[#9CA3AF] mt-0.5 truncate">{Object.values(i.selected_options).join(" · ")}</div>
                          )}
                          <div className="font-mono text-xs text-neon mt-1">{money(i.price)}</div>
                          <div className="flex items-center gap-3 mt-2">
                            <button onClick={() => setQty(k, i.quantity - 1)} data-testid={`qty-dec-${i.source}-${i.ext_id}`} className="border border-[#333] p-1 hover:border-neon"><Minus size={12} /></button>
                            <span className="font-mono text-sm w-6 text-center">{i.quantity}</span>
                            <button onClick={() => setQty(k, i.quantity + 1)} data-testid={`qty-inc-${i.source}-${i.ext_id}`} className="border border-[#333] p-1 hover:border-neon"><Plus size={12} /></button>
                            <button onClick={() => remove(k)} data-testid={`cart-remove-${i.source}-${i.ext_id}`} className="ml-auto text-[#9CA3AF] hover:text-red-400"><X size={16} /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="p-6 border-t border-[#222]">
                <div className="flex justify-between font-mono text-sm mb-4">
                  <span className="text-[#9CA3AF] uppercase tracking-[0.2em]">Subtotal</span>
                  <span className="font-display text-2xl text-neon">{money(total)}</span>
                </div>
                <button data-testid="checkout-btn" disabled={!items.length || checkingOut} onClick={checkout} className="w-full bg-neon text-void font-bold uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 hover:bg-[#FAFAFA] transition-colors disabled:opacity-40">
                  {checkingOut ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : "Checkout"}
                </button>
                <p className="mt-3 font-mono text-[10px] text-[#555] text-center uppercase tracking-widest">Secure payment via Stripe · Test mode</p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
